/**
 * Prompt Fragments System
 * 
 * This composable manages reusable prompt fragments that can be combined
 * to create complete system prompts. Fragments are now stored in the database
 * and fetched dynamically.
 */

import { normalizeCategory, CATEGORY_ORDER } from '#shared/prompt-fragments-core'
export { normalizeCategory } from '#shared/prompt-fragments-core'
import type { PromptFragmentCategory } from '#shared/prompt-fragments-core'
export type { PromptFragmentCategory } from '#shared/prompt-fragments-core'

export interface PromptFragment {
  id: string
  name: string
  description: string
  category: PromptFragmentCategory
  content: string
  organization?: string
  isRequired?: boolean
  isActive?: boolean
  sortOrder?: number
}

// Category utilities (shared by admin page and editor)
// normalizeCategory is imported from shared core

export const CATEGORY_LABELS: Record<PromptFragmentCategory, string> = {
  system: 'System',
  style: 'Style',
  safety: 'Safety',
  format: 'Format',
  structure: 'Structure',
  modifier: 'Modifier'
}

export const CATEGORY_ICONS: Record<PromptFragmentCategory, string> = {
  system: 'i-heroicons-cog-6-tooth',
  style: 'i-heroicons-swatch',
  safety: 'i-heroicons-shield-check',
  format: 'i-heroicons-document-text',
  structure: 'i-heroicons-rectangle-group',
  modifier: 'i-heroicons-adjustments-horizontal'
}

export const CATEGORY_COLORS: Record<PromptFragmentCategory, 'primary' | 'info' | 'warning' | 'error' | 'blue' | 'neutral'> = {
  system: 'info',
  style: 'primary',
  safety: 'error',
  format: 'warning',
  structure: 'blue',
  modifier: 'neutral'
}

export const getCategoryLabel = (category: string) => CATEGORY_LABELS[normalizeCategory(category)]
export const getCategoryIcon = (category: string) => CATEGORY_ICONS[normalizeCategory(category)]
export const getCategoryColor = (category: string) => CATEGORY_COLORS[normalizeCategory(category)]

// Options and tabs for UI consumers
export const categoryOptions: Array<{ label: string; value: PromptFragmentCategory }> = [
  { label: 'System', value: 'system' },
  { label: 'Safety', value: 'safety' },
  { label: 'Style', value: 'style' },
  { label: 'Format', value: 'format' },
  { label: 'Modifier', value: 'modifier' }
]

export const categoryOptionsWithAll: Array<{ label: string; value: PromptFragmentCategory | null }> = [
  { label: 'All Categories', value: null },
  ...categoryOptions
]

export const categoryTabs: Array<{ key: PromptFragmentCategory; label: string; icon: string }> = categoryOptions.map(o => ({
  key: o.value,
  label: o.label,
  icon: CATEGORY_ICONS[o.value]
}))

export interface AssistantPromptConfig {
  userEditablePrompt: string
  selectedFragmentIds: string[]
}

export function usePromptFragments() {
  // State for cached fragments
  const fragments = useState<PromptFragment[]>('promptFragments', () => [])
  const isLoading = useState<boolean>('promptFragmentsLoading', () => false)
  const lastFetch = useState<number>('promptFragmentsLastFetch', () => 0)
  
  // Cache duration: 5 minutes
  const CACHE_DURATION = 5 * 60 * 1000
  
  // Fetch fragments from database
  const fetchFragments = async (force = false) => {
    const now = Date.now()
    
    // Use cache if available and not forced
    if (!force && fragments.value.length > 0 && (now - lastFetch.value < CACHE_DURATION)) {
      return fragments.value
    }
    
    isLoading.value = true
    try {
      const response = await $fetch('/api/prompt-fragments/list', {
        query: {
          includeInactive: false
        }
      })
      
      if (response.success && response.fragments) {
        fragments.value = response.fragments
        lastFetch.value = now
      }
      
      return fragments.value
    } catch (error) {
      console.error('Error fetching prompt fragments:', error)
      // Return cached fragments if available
      return fragments.value
    } finally {
      isLoading.value = false
    }
  }
  
  // Get all fragments (with caching)
  const getAllFragments = async () => {
    // Always run through fetchFragments to respect TTL-based caching.
    // This avoids stale data when cache has expired without forcing a network request on every call.
    await fetchFragments()
    return fragments.value
  }
  
  // Get fragment by ID
  const getFragmentById = async (id: string) => {
    // First check cache
    const cached = fragments.value.find(f => f.id === id)
    if (cached) return cached
    
    // If not in cache, fetch from API
    try {
      const response = await $fetch(`/api/prompt-fragments/${id}`)
      if (response.success && response.fragment) {
        // Add to cache
        const existing = fragments.value.findIndex(f => f.id === id)
        if (existing >= 0) {
          fragments.value[existing] = response.fragment
        } else {
          fragments.value.push(response.fragment)
        }
        return response.fragment
      }
    } catch (error) {
      console.error(`Error fetching fragment ${id}:`, error)
    }
    return null
  }
  
  // Get fragments by category
  const getFragmentsByCategory = async (category: PromptFragment['category']) => {
    const allFragments = await getAllFragments()
    return allFragments.filter(f => f.category === category)
  }
  
  // Get fragments by organization
  const getFragmentsByOrganization = async (organization: string) => {
    const allFragments = await getAllFragments()
    return allFragments.filter(f => 
      !f.organization || f.organization === organization
    )
  }
  
  // Reconstruct full system prompt from fragments
  const reconstructSystemPrompt = async (config: AssistantPromptConfig): Promise<string> => {
    if (!config) {
      return ''
    }
    
    // Ensure fragments cache is warm to determine required fragments
    const all = await getAllFragments()

    // Always include required fragments regardless of user selection
    const requiredIds = all.filter(f => f.isRequired).map(f => f.id)

    // Combine selected + required, ensuring uniqueness
    const uniqueIds = Array.from(new Set([...(config.selectedFragmentIds || []), ...requiredIds]))

    // Fetch combined fragments
    const selectedFragments: PromptFragment[] = []
    if (uniqueIds.length > 0) {
      const fetched = await Promise.all(uniqueIds.map(id => getFragmentById(id)))
      for (let i = 0; i < uniqueIds.length; i++) {
        const id = uniqueIds[i]
        const f = fetched[i]
        if (!f) {
          console.warn(`[usePromptFragments] Referenced fragment "${id}" not found. It may have been deleted. Skipping.`)
          continue
        }
        if (!f.content || !f.content.trim()) {
          console.warn(`[usePromptFragments] Fragment "${id}" has no content. Skipping.`)
          continue
        }
        selectedFragments.push(f)
      }
    }

    // Sort by the new category order
    selectedFragments.sort((a, b) => CATEGORY_ORDER.indexOf(a.category) - CATEGORY_ORDER.indexOf(b.category))

    // Compose: userEditablePrompt first, then ordered fragments
    const promptParts: string[] = []
    if (config.userEditablePrompt?.trim()) {
      promptParts.push(config.userEditablePrompt.trim())
    }
    for (const frag of selectedFragments) {
      promptParts.push(frag.content.trim())
    }

    // Join with double newlines for clarity
    return promptParts.join('\n\n')
  }
  
  // Parse existing system prompt to extract user-editable part and fragments
  // This is useful for migrating existing prompts to the new fragment system
  const parseExistingPrompt = async (fullPrompt: any): Promise<AssistantPromptConfig> => {
    // Normalize various content shapes (string | array | object) to a plain string
    const normalizeToString = (value: any): string => {
      if (typeof value === 'string') return value
      if (Array.isArray(value)) {
        // Join array parts, supporting objects with text/content fields
        return value
          .map((p: any) =>
            typeof p === 'string'
              ? p
              : typeof p?.text === 'string'
                ? p.text
                : typeof p?.content === 'string'
                  ? p.content
                  : ''
          )
          .filter(Boolean)
          .join('\n')
          .trim()
      }
      if (value && typeof value === 'object') {
        if (typeof (value as any).text === 'string') return (value as any).text
        if (typeof (value as any).content === 'string') return (value as any).content
      }
      return String(value ?? '')
    }

    const promptStr = normalizeToString(fullPrompt)

    if (!promptStr) {
      return {
        userEditablePrompt: '',
        selectedFragmentIds: []
      }
    }
    
    const allFragments = await getAllFragments()
    const foundFragmentIds: string[] = []
    let remainingPrompt = promptStr
    
    // Try to identify which fragments are present in the prompt
    for (const fragment of allFragments) {
      if (fragment.content && promptStr.includes(fragment.content)) {
        foundFragmentIds.push(fragment.id)
        // Remove the fragment content from the remaining prompt
        remainingPrompt = remainingPrompt.replace(fragment.content, '').trim()
      }
    }
    
    // Clean up any extra whitespace
    remainingPrompt = remainingPrompt.replace(/\n{3,}/g, '\n\n').trim()
    return {
      userEditablePrompt: remainingPrompt,
      selectedFragmentIds: foundFragmentIds
    }
  }
  
  // Get recommended fragments for a given domain/organization
  const getRecommendedFragments = async (organization?: string, domain?: string): Promise<string[]> => {
    const recommendations: string[] = []
    
    // Always recommend citation format
    recommendations.push('citation-basic')
    
    // Healthcare organizations should have safety guidelines
    if (organization === 'singhealth' || domain === 'healthcare') {
      recommendations.push('healthcare-safety')
      recommendations.push('professional-tone')
      recommendations.push('singhealth-context')
    }
    
    // Domain-specific recommendations
    if (domain === 'radiology') {
      recommendations.push('radiology-expertise')
    } else if (domain === 'hr') {
      recommendations.push('hr-expertise')
    }
    
    // General best practices
    recommendations.push('uncertainty-handling')
    recommendations.push('structured-response')
    
    return [...new Set(recommendations)] // Remove duplicates
  }
  
  // Update a fragment (admin only)
  const updateFragment = async (id: string, updates: Partial<PromptFragment>) => {
    try {
      const response = await $fetch(`/api/prompt-fragments/${id}`, {
        method: 'PATCH',
        body: updates
      })
      
      if (response.success && response.fragment) {
        // Update cache
        const index = fragments.value.findIndex(f => f.id === id)
        if (index >= 0) {
          fragments.value[index] = response.fragment
        }
        return response.fragment
      }
    } catch (error) {
      console.error('Error updating fragment:', error)
      throw error
    }
  }
  
  // Force refresh fragments from database
  const refreshFragments = () => fetchFragments(true)

  // Invalidate TTL without fetching immediately (next read will refetch)
  const invalidateFragmentsCache = () => {
    lastFetch.value = 0
  }
  
  return {
    fragments: readonly(fragments),
    isLoading: readonly(isLoading),
    getAllFragments,
    getFragmentById,
    getFragmentsByCategory,
    getFragmentsByOrganization,
    reconstructSystemPrompt,
    parseExistingPrompt,
    getRecommendedFragments,
    updateFragment,
    refreshFragments,
    invalidateFragmentsCache
  }
}

// Legacy standalone exports removed. All APIs are provided via usePromptFragments().
